import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Settings, FileText, Quote, Users, Image as ImageIcon, KeyRound, Globe, Map, BookOpen, Inbox, Download, LogOut, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/studio", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/studio/enquiries", label: "Enquiries", icon: Inbox, badge: "new_enquiries" },
  { to: "/studio/pages", label: "Pages", icon: FileText },
  { to: "/studio/destinations", label: "Destinations", icon: Globe },
  { to: "/studio/journeys", label: "Journeys", icon: Map },
  { to: "/studio/journal", label: "Journal", icon: BookOpen },
  { to: "/studio/media", label: "Media", icon: ImageIcon },
  { to: "/studio/testimonials", label: "Testimonials", icon: Quote },
  { to: "/studio/team", label: "Team", icon: Users },
  { to: "/studio/site-settings", label: "Site Settings", icon: Settings },
  { to: "/studio/activity", label: "Activity", icon: ShieldCheck },
  { to: "/studio/export", label: "Export", icon: Download },
  { to: "/studio/account", label: "Account", icon: KeyRound },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [newEnquiries, setNewEnquiries] = useState(0);

  const refreshBadge = useCallback(async () => {
    try {
      const r = await api.get("/admin/enquiries", { params: { status: "new" } });
      setNewEnquiries((r.data || []).length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshBadge();
    const id = setInterval(refreshBadge, 60_000); // refresh once a minute
    return () => clearInterval(id);
  }, [refreshBadge]);

  const onLogout = async () => {
    await logout();
    navigate("/studio/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ivory text-ink flex">
      <Helmet>
        <title>Studio — Abbot Asia</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="w-64 bg-jade text-ivory min-h-screen flex flex-col">
        <div className="px-6 py-8 border-b border-ivory/15">
          <p className="font-serif text-2xl">Abbot <span className="italic text-copper">Asia</span></p>
          <p className="mt-1 text-[10px] tracking-[0.22em] uppercase text-ivory/55">Studio</p>
        </div>
        <nav className="flex-1 py-6">
          {NAV.map(({ to, label, icon: Icon, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-[13px] tracking-[0.06em] transition-colors ${
                  isActive
                    ? "bg-ivory/10 text-ivory border-l-2 border-copper"
                    : "text-ivory/70 hover:text-ivory hover:bg-ivory/5 border-l-2 border-transparent"
                }`
              }
            >
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {badge === "new_enquiries" && newEnquiries > 0 && (
                <span
                  data-testid="nav-badge-new-enquiries"
                  className="text-[10px] tracking-[0.1em] uppercase bg-copper text-ivory rounded-full px-2 py-0.5 leading-none"
                >
                  {newEnquiries}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-6 border-t border-ivory/15">
          <p className="text-[11px] tracking-[0.12em] uppercase text-ivory/50">Signed in as</p>
          <p className="mt-1 text-[14px] text-ivory truncate" data-testid="admin-current-user">{user?.email}</p>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="mt-4 flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ivory/65 hover:text-copper transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-[1100px] mx-auto px-10 py-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
